import type { SuggestedEditKind } from '@aline/common';
import { PLAYBOOK_TONES } from 'src/editor/toolbars/right-panel/ai/playbooks/playbook-colors';
import type { PlaybookTone } from 'src/editor/toolbars/right-panel/ai/playbooks/playbook-colors';

/**
 * The kind colours: green for an add, amber for a change, red for a delete.
 * The current stop's index badge and ghost outline use the blue "reviewing"
 * tone, so selection reads apart from the kind. Add and delete are stated
 * rather than taken from the panel's "passed" and "failed" tones: their
 * surfaces sit one shade under the matching card tint, so a chip still
 * stands out on a tinted card, and delete is a pinker red so it reads as a
 * pending removal rather than a failure.
 */
export const SUGGESTION_TONES: Record<SuggestedEditKind, PlaybookTone> = {
  add: { background: '#e3f4e1', border: '#bfe3bd', color: '#15803d' },
  change: PLAYBOOK_TONES.review,
  delete: { background: '#fae3e3', border: '#f7cbca', color: '#d42d23' },
};

export const CURRENT_STOP_TONE: PlaybookTone = PLAYBOOK_TONES.reviewing;

/**
 * A pending move, as the pill on a position card shows it. Violet at the
 * same steps as the amber change tone, so it matches that tone's lightness
 * and saturation while reading as its own kind.
 */
export const MOVE_TONE: PlaybookTone = {
  background: '#ede9fe',
  border: '#c4b5fd99',
  color: '#6d28d9',
};

/**
 * Nothing pending: a dark slate gray on a soft slate block. The text is about
 * 5.5:1 against the block, so a small label stays easy to read and does not
 * look disabled beside the coloured tones.
 */
export const NEUTRAL_TONE: PlaybookTone = {
  background: '#eff3f7',
  border: '#dfe5ec',
  color: '#55636e',
};

/**
 * Where a pending move would land: no fill, since nothing is there yet, with
 * the neutral gray text inside the move tone's border so it still pairs with
 * its source.
 */
export const MOVE_DESTINATION_TONE: PlaybookTone = {
  background: 'transparent',
  border: MOVE_TONE.border,
  color: NEUTRAL_TONE.color,
};

/** What a suggestion pill can name: the three edit kinds, a move, or where a move lands. */
export type SuggestionPillKind = SuggestedEditKind | 'move' | 'moveDestination';

export const SUGGESTION_PILL_TONES: Record<SuggestionPillKind, PlaybookTone> = {
  ...SUGGESTION_TONES,
  move: MOVE_TONE,
  moveDestination: MOVE_DESTINATION_TONE,
};

/**
 * A pill's tone while its suggestion is the current stop. Only the
 * destination of a move changes: at rest it is an empty outline, and as the
 * current stop it fills in the move tone, so both ends of the move look alike.
 */
export const SUGGESTION_PILL_CURRENT_TONES: Record<SuggestionPillKind, PlaybookTone> = {
  ...SUGGESTION_PILL_TONES,
  moveDestination: MOVE_TONE,
};

export const SUGGESTION_CHIP_LABELS: Record<SuggestedEditKind, string> = {
  add: 'AI Added',
  change: 'AI Edited',
  delete: 'AI Removed',
};

/**
 * The surface of a position card carrying a suggestion: a tint one step
 * lighter than the kind tone under a border in the kind colour, so the card
 * reads as pending from a distance without drowning the text. `currentBorder`
 * is that border one shade darker, for the card that is the current stop.
 */
export const SUGGESTION_CARD_TONES: Record<
  SuggestedEditKind,
  { background: string; border: string; currentBorder: string }
> = {
  add: { background: '#f6fdf5', border: '#bfe3bd', currentBorder: '#98d294' },
  change: { background: '#fefbf4', border: '#eace5a', currentBorder: '#d9b53a' },
  delete: { background: '#fdf7f6', border: '#f7cbca', currentBorder: '#f0aaa8' },
};

/**
 * A pill's border while its suggestion is the current stop, one shade darker
 * than its resting border: the card's `currentBorder` for an edit kind, and
 * the same step down the violet for either end of a move.
 */
export const SUGGESTION_PILL_CURRENT_BORDERS: Record<SuggestionPillKind, string> = {
  add: SUGGESTION_CARD_TONES.add.currentBorder,
  change: SUGGESTION_CARD_TONES.change.currentBorder,
  delete: SUGGESTION_CARD_TONES.delete.currentBorder,
  move: '#a78bfa',
  moveDestination: '#a78bfa',
};

/**
 * The review bar across the top of the page. It sits on the yellow of an
 * edited position card, so the bar and the cards it steps through read as
 * one thing, and its buttons hover in the change chip's yellow, one step
 * darker in the same family. Its text and decision colours come from the
 * theme, so only the surfaces and the icon are stated here.
 */
export const SUGGESTIONS_BAR_COLORS = {
  background: SUGGESTION_CARD_TONES.change.background,
  hover: SUGGESTION_TONES.change.background,
  icon: '#ff9100',
} as const;

/** Inline diff colours: removed text struck through on red, added text on green. */
export const DIFF_TONES = {
  added: { background: '#e7fce4', color: '#3c7742' },
  removed: { background: '#fae3e3', color: '#a54440' },
} as const;
